/**
 * Post-login redirect helpers.
 *
 * Protected routes send visitors to the sign-in screen with the originally
 * requested path in a `redirect` query param. These helpers keep that param
 * safe (internal paths only, no open redirects) and consistent everywhere it
 * is written or read.
 *
 * After auth, public marketing chrome (`/`, `/learn`, `/bgmi`, …) is rewritten
 * into the authenticated `/app/*` shell so learners never lose the sidebar.
 */

const DEFAULT_TARGET = "/app/home";

/** Paths that should never be used as a post-login destination. */
const EXCLUDED_PREFIXES = ["/login", "/auth", "/welcome", "/forgot-password", "/reset-password"];

/** Split pathname / search / hash so rewrites can preserve query + hash. */
function splitPath(value: string): { pathname: string; search: string; hash: string } {
  const hashIdx = value.indexOf("#");
  const hash = hashIdx >= 0 ? value.slice(hashIdx) : "";
  const withoutHash = hashIdx >= 0 ? value.slice(0, hashIdx) : value;
  const qIdx = withoutHash.indexOf("?");
  const search = qIdx >= 0 ? withoutHash.slice(qIdx) : "";
  const pathname = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
  return { pathname, search, hash };
}

/**
 * Map public marketing destinations to authenticated shell routes.
 * Preserves query string and hash.
 */
export function rewritePublicMarketingToAppShell(path: string): string {
  const { pathname, search, hash } = splitPath(path);
  const suffix = `${search}${hash}`;

  if (pathname === "/learn") {
    return `/app/browse${suffix}`;
  }
  if (pathname.startsWith("/learn/")) {
    const rest = pathname.slice("/learn/".length);
    return `/app/pathways/${rest}${suffix}`;
  }
  if (pathname === "/" || pathname === "/bgmi" || pathname === "/free-fire") {
    return `/app/home${suffix}`;
  }
  return path;
}

/** Returns a safe internal path, or "" when the value can't be trusted. */
export function sanitizeRedirect(value: string | null | undefined): string {
  if (!value) return "";
  let path = value;
  try {
    path = decodeURIComponent(value);
  } catch {
    /* value was not encoded */
  }
  if (!path.startsWith("/")) return "";
  if (path.startsWith("//") || path.startsWith("/\\")) return "";
  if (EXCLUDED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`))) {
    return "";
  }
  return path;
}

/** Resolve where a user should land after signing in. */
export function resolvePostAuthTarget(
  value: string | null | undefined,
  fallback: string = DEFAULT_TARGET,
): string {
  const safe = sanitizeRedirect(value);
  if (!safe) return fallback;
  return rewritePublicMarketingToAppShell(safe);
}

/** Build a sign-in URL that preserves the current location. */
export function buildSignInPath(
  location: { pathname: string; search?: string },
  base: "/login" | "/auth" = "/login",
  extraParams?: Record<string, string>,
): string {
  const current = sanitizeRedirect(`${location.pathname}${location.search ?? ""}`);
  const params = new URLSearchParams(extraParams);
  if (current) params.set("redirect", current);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Carry an existing `redirect` param across auth screens (e.g. forgot password). */
export function withPreservedRedirect(path: string, search: string): string {
  const redirect = sanitizeRedirect(new URLSearchParams(search).get("redirect"));
  return redirect ? `${path}?redirect=${encodeURIComponent(redirect)}` : path;
}

/**
 * Confirmation / magic-link `emailRedirectTo` must land on the public
 * `/auth/callback` route. Sending learners to `/app/home` (K100) lets
 * `ProtectedRoute` strip the session hash/`code` before Auth can exchange it.
 */
export function signupEmailConfirmRedirect(
  origin: string,
  safeInternalRedirect = "",
): string {
  const base = `${origin.replace(/\/$/, "")}/auth/callback`;
  const dest = sanitizeRedirect(safeInternalRedirect);
  if (!dest || dest === DEFAULT_TARGET) return base;
  return `${base}?redirect=${encodeURIComponent(dest)}`;
}
